(() => {
  const MM_PER_CM = 10;
  const FALLBACK_MOTIF_MM = 50;
  // Matches the thickness slider max: the stage always reserves room for it so a
  // 15 cm motif + 5 mm outline on each side (16 cm finished) is never clipped.
  const MAX_OUTLINE_MM = 5;
  const RING_STEPS = 16;
  const MAX_OUTLINE_PX = 160;
  const MAX_DEVICE_PIXEL_RATIO = 2;
  const PROBE_SIZE = 96;
  const TRANSPARENCY_THRESHOLD = 0.01;
  const OUTLINE_INK = { white: '#ffffff', black: '#000000' };

  const parseOptionNumber = (value) => {
    const normalized = String(value).replace(/\s/g, '').replace(',', '.');
    const match = normalized.match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : 0;
  };

  // Option values carry their unit: "7,5 cm" or "75 mm". Everything downstream works in millimetres.
  const parseSizeMm = (value) => {
    const amount = parseOptionNumber(value);
    if (!amount) return 0;
    return /mm/i.test(String(value)) ? amount : amount * MM_PER_CM;
  };

  const findVariant = (variants, selectedOptions) =>
    variants.find(
      (variant) =>
        variant.options.length === selectedOptions.length &&
        variant.options.every((option, index) => option === selectedOptions[index])
    );

  const formatPrice = (cents, locale, currency) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);

  const hasOutline = (color) => color !== 'none';

  // The quantity and size axes are recognised by what their values look like, never by their
  // name: names get translated ("Quantity" becomes "Quantité") while a size value keeps its
  // unit and a quantity value stays a bare integer in every language (issue #21).
  const looksLikeSize = (value) => /(cm|mm)/i.test(String(value)) && parseOptionNumber(value) > 0;
  const looksLikeQuantity = (value) => /^\d+$/.test(String(value).replace(/[\s\u00a0\u202f]/g, ''));

  const detectAxisIndexes = (optionValues) => {
    const size = optionValues.findIndex((values) => values.length > 0 && looksLikeSize(values[0]));
    const quantity = optionValues.findIndex(
      (values, index) => index !== size && values.length > 0 && looksLikeQuantity(values[0])
    );
    return { quantity, size };
  };

  // What each quantity tier costs, and what it saves against the cheapest-per-unit small tier.
  // Every figure comes from a variant price: nothing is computed into existence here.
  const quantityTiers = (variants, selectedOptions, quantityIndex, values) => {
    const tiers = values.map((value) => {
      const candidate = [...selectedOptions];
      candidate[quantityIndex] = value;
      const variant = findVariant(variants, candidate);
      const quantity = parseOptionNumber(value);
      return {
        value,
        variant,
        quantity,
        unit: variant && quantity ? variant.price / quantity : 0,
        saved: 0,
      };
    });

    const reference = tiers
      .filter((tier) => tier.unit > 0)
      .sort((first, second) => first.quantity - second.quantity)[0];
    if (!reference) return tiers;

    return tiers.map((tier) => ({
      ...tier,
      saved: tier.unit > 0 ? Math.round((1 - tier.unit / reference.unit) * 100) : 0,
    }));
  };

  // The uploaded SVG is never parsed into the DOM: we only read its root tag as text to
  // recover an aspect ratio, because a sizeless SVG draws unpredictably into a canvas.
  const readSvgGeometry = (source) => {
    const hasForeignObject = /<foreignObject[\s/>]/i.test(source);
    const root = source.match(/<svg\b[^>]*>/i);
    if (!root) return { ratio: 0, hasForeignObject };

    const attribute = (name) => {
      const match = root[0].match(new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, 'i'));
      const value = match ? match[2] ?? match[3] : '';
      return value.includes('%') ? '' : value;
    };

    const viewBox = attribute('viewBox').trim().split(/[\s,]+/).map(Number);
    if (viewBox.length === 4 && viewBox[2] > 0 && viewBox[3] > 0) {
      return { ratio: viewBox[2] / viewBox[3], hasForeignObject };
    }

    const width = parseOptionNumber(attribute('width'));
    const height = parseOptionNumber(attribute('height'));
    if (width > 0 && height > 0) return { ratio: width / height, hasForeignObject };

    return { ratio: 0, hasForeignObject };
  };

  // Share of pixels that are not fully opaque. A flattened PNG sits at ~0.
  const transparencyRatio = (data) => {
    if (!data || data.length < 4) return 0;
    let transparent = 0;
    for (let index = 3; index < data.length; index += 4) {
      if (data[index] < 250) transparent += 1;
    }
    return transparent / (data.length / 4);
  };

  // Everything the preview needs, in device-independent pixels. `motifMm` is the chosen
  // size of the artwork; the outline is a border ADDED around it, so the finished sticker
  // is motif + 2 × thickness. Scale is constant across the size range and always leaves
  // room for the slider's max outline so the stage never clips the border.
  const previewGeometry = ({
    motifMm,
    thicknessMm,
    ratio,
    maxMotifMm,
    stagePx,
    maxOutlineMm = MAX_OUTLINE_MM,
  }) => {
    const motif = motifMm > 0 ? motifMm : FALLBACK_MOTIF_MM;
    const largestMotif = Math.max(maxMotifMm > 0 ? maxMotifMm : motif, motif);
    const outlineMm = Math.max(thicknessMm, 0);
    const reservedOutlineMm = Math.max(maxOutlineMm, 0);
    const largestFinished = largestMotif + reservedOutlineMm * 2;
    const scale = stagePx / largestFinished;
    const safeRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
    const artWidthMm = safeRatio >= 1 ? motif : motif * safeRatio;
    const artHeightMm = safeRatio >= 1 ? motif / safeRatio : motif;
    const finishedWidthMm = artWidthMm + outlineMm * 2;
    const finishedHeightMm = artHeightMm + outlineMm * 2;

    return {
      scale,
      outlineMm,
      finishedMm: Math.max(finishedWidthMm, finishedHeightMm),
      outline: outlineMm * scale,
      artWidth: artWidthMm * scale,
      artHeight: artHeightMm * scale,
      width: finishedWidthMm * scale,
      height: finishedHeightMm * scale,
    };
  };

  // Physical lengths shown under the preview: motif stays as the option label, finished
  // size is derived in millimetres and rendered in cm with the active locale.
  const formatCm = (mm, locale) => {
    const cm = mm / MM_PER_CM;
    const digits = Number.isInteger(cm) ? 0 : 1;
    const value = cm.toLocaleString(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: 1,
    });
    return `${value} cm`;
  };

  const formatMm = (mm, locale) => {
    const value = Number(mm).toLocaleString(locale, { maximumFractionDigits: 1 });
    return `${value} mm`;
  };

  // Alpha dilation: the artwork silhouette, flooded with the outline ink, is redrawn along a
  // circle of offsets. The radius grows by doubling so a thin stroke never ends up with a hole
  // in the middle of its own outline, and a 5 mm outline still costs a handful of passes.
  const drawOutline = (context, image, geometry, ink, pixelRatio) => {
    const radius = Math.min(geometry.outline * pixelRatio, MAX_OUTLINE_PX);
    if (!(radius >= 0.5)) return;

    const { width, height } = context.canvas;
    const silhouette = document.createElement('canvas');
    silhouette.width = width;
    silhouette.height = height;

    const silhouetteContext = silhouette.getContext('2d');
    silhouetteContext.drawImage(
      image,
      geometry.outline * pixelRatio,
      geometry.outline * pixelRatio,
      geometry.artWidth * pixelRatio,
      geometry.artHeight * pixelRatio
    );
    silhouetteContext.globalCompositeOperation = 'source-in';
    silhouetteContext.fillStyle = ink;
    silhouetteContext.fillRect(0, 0, width, height);
    silhouetteContext.globalCompositeOperation = 'source-over';

    const scratch = document.createElement('canvas');
    scratch.width = width;
    scratch.height = height;
    const scratchContext = scratch.getContext('2d');

    let grown = 0;
    while (grown < radius) {
      const step = Math.min(Math.max(grown, 1), radius - grown);
      scratchContext.clearRect(0, 0, width, height);
      scratchContext.drawImage(silhouette, 0, 0);
      for (let index = 0; index < RING_STEPS; index += 1) {
        const angle = (index * 2 * Math.PI) / RING_STEPS;
        scratchContext.drawImage(silhouette, Math.cos(angle) * step, Math.sin(angle) * step);
      }
      silhouetteContext.clearRect(0, 0, width, height);
      silhouetteContext.drawImage(scratch, 0, 0);
      grown += step;
    }

    context.drawImage(silhouette, 0, 0);
  };

  globalThis.StickerConfiguratorUtils = {
    parseOptionNumber,
    parseSizeMm,
    findVariant,
    formatPrice,
    formatCm,
    formatMm,
    hasOutline,
    detectAxisIndexes,
    quantityTiers,
    readSvgGeometry,
    transparencyRatio,
    previewGeometry,
    MAX_OUTLINE_MM,
  };

  if (typeof customElements === 'undefined' || customElements.get('sticker-configurator')) return;

  customElements.define(
    'sticker-configurator',
    class StickerConfigurator extends HTMLElement {
      connectedCallback() {
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
        this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
        this.variantInput = this.querySelector('[name="id"]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        this.totalPrice = this.querySelector('[data-total-price]');
        this.unitPrice = this.querySelector('[data-unit-price]');
        this.fileInput = this.querySelector('[data-file-input]');
        this.fileError = this.querySelector('[data-file-error]');
        this.outlineThickness = this.querySelector('[data-outline-thickness]');
        this.thicknessInputs = [...this.querySelectorAll('[data-outline-thickness-input]')];
        this.tierGroup = this.querySelector('[data-tiers]');
        this.dropzone = this.querySelector('[data-dropzone]');
        this.fileName = this.querySelector('[data-file-name]');
        this.stage = this.querySelector('[data-preview-stage]');
        this.canvas = this.querySelector('[data-preview-canvas]');
        this.stageMessage = this.querySelector('[data-preview-message]');
        this.previewScale = this.querySelector('[data-preview-scale]');
        this.previewNotices = this.querySelector('[data-preview-notices]');
        this.optionGroups = [...this.querySelectorAll('[data-option-position]')];
        this.quantityOptionIndex = Number(this.dataset.quantityOptionIndex);
        this.sizeOptionIndex = Number(this.dataset.sizeOptionIndex);

        // Safety net for issue #21: if the server-side detection failed (options renamed or
        // translated after render), re-detect from the rendered values — they are translated
        // along with the page, and their shape survives any language.
        if (this.quantityOptionIndex < 0 || this.sizeOptionIndex < 0) {
          const detected = detectAxisIndexes(
            this.optionGroups.map((group) => [...group.querySelectorAll('input')].map((input) => input.value))
          );
          if (this.quantityOptionIndex < 0) this.quantityOptionIndex = detected.quantity;
          if (this.sizeOptionIndex < 0) this.sizeOptionIndex = detected.size;
        }
        // Never a silent failure: an unidentified size axis is said under the preview, an
        // unidentified quantity axis is said where the unit price would have been.
        this.staticNotices = this.sizeOptionIndex < 0 ? ['scale_unknown'] : [];

        this.artwork = null;
        this.artworkRatio = 1;
        this.previewUrl = '';
        this.fileNotices = [];
        this.frame = 0;

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-outline-color]')) this.updateOutline(event.target);
          if (event.target.matches('[data-outline-thickness-input]')) this.schedulePreview();
          if (event.target.matches('[data-file-input]')) this.loadArtwork(event.target.files[0]);
          if (event.target.matches('[data-preview-background-input]')) {
            this.stage.dataset.previewBackground = event.target.value;
          }
          if (event.target.closest('[data-option-position]')) this.updateVariant();
        });
        this.bindDropzone();
        // The form carries `novalidate`, inherited from Dawn, so `required` alone would let an
        // order through without artwork. Capturing on the ancestor runs before the product-form
        // submit handler, whatever order the two elements are upgraded in.
        this.addEventListener('submit', (event) => this.requireArtwork(event), true);
        if (typeof ResizeObserver !== 'undefined') {
          this.stageObserver = new ResizeObserver(() => this.schedulePreview());
          this.stageObserver.observe(this.stage);
        }

        this.updateOutline(this.querySelector('[data-outline-color]:checked'));
        this.updateVariant();
      }

      disconnectedCallback() {
        this.stageObserver?.disconnect();
        this.releaseArtwork();
      }

      get selectedOptions() {
        return this.optionGroups.map((group) => group.querySelector('input:checked')?.value || '');
      }

      get outlineColor() {
        return this.querySelector('[data-outline-color]:checked')?.dataset.outlineColor || 'none';
      }

      get thicknessMm() {
        const selected = this.thicknessInputs.find((input) => input.checked && !input.disabled);
        return selected ? Number(selected.value) : 0;
      }

      get selectedSizeValue() {
        return this.sizeOptionIndex >= 0 ? this.selectedOptions[this.sizeOptionIndex] : '';
      }

      get largestSizeMm() {
        if (this.sizeOptionIndex < 0) return 0;
        const values = [...this.optionGroups[this.sizeOptionIndex].querySelectorAll('input')];
        return values.reduce((largest, input) => Math.max(largest, parseSizeMm(input.value)), 0);
      }

      updateVariant() {
        const selectedOptions = this.selectedOptions;
        const variant = findVariant(this.variants, selectedOptions);
        const canAdd = Boolean(variant?.available);

        this.currentVariant = variant;
        this.variantInput.value = variant?.id || '';
        this.variantInput.disabled = !canAdd;

        this.updateAvailability(selectedOptions);
        this.updateTiers(selectedOptions);
        this.updateSubmitState();
        this.schedulePreview();

        if (!variant) {
          this.totalPrice.textContent = this.dataset.unavailableLabel;
          this.unitPrice.hidden = true;
          return;
        }

        this.totalPrice.textContent = formatPrice(variant.price, this.dataset.locale, this.dataset.currency);
        const quantity = parseOptionNumber(selectedOptions[this.quantityOptionIndex]);

        if (this.quantityOptionIndex < 0 || !quantity) {
          // Issue #21: the unit price never just vanishes — its absence is stated in place.
          const message = this.strings.unit_price_unknown || '';
          this.unitPrice.textContent = message;
          this.unitPrice.hidden = !message;
          return;
        }

        const price = formatPrice(variant.price / quantity, this.dataset.locale, this.dataset.currency);
        this.unitPrice.textContent = this.dataset.unitPriceLabel.replace('__PRICE__', price);
        this.unitPrice.hidden = false;
      }

      // One place decides whether the order can be placed, and says which blocker is in the way.
      updateSubmitState() {
        const canAdd = Boolean(this.currentVariant?.available);
        const hasFile = this.fileInput.files.length > 0;

        this.submitButton.disabled = !canAdd || !hasFile;
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButtonText.classList.remove('hidden');
        this.soldOutMessage.classList.add('hidden');

        if (!this.currentVariant) {
          this.submitButtonText.textContent = this.dataset.unavailableLabel;
        } else if (!canAdd) {
          this.submitButtonText.textContent = this.dataset.soldOutLabel;
        } else {
          this.submitButtonText.textContent = hasFile ? this.dataset.addLabel : this.strings.missing_cta;
        }
      }

      // Every tier shows what it costs and what it saves, so the next price break is visible
      // without having to click through the options one by one.
      updateTiers(selectedOptions) {
        if (!this.tierGroup || this.quantityOptionIndex < 0) return;

        const inputs = [...this.tierGroup.querySelectorAll('input')];
        const tiers = quantityTiers(
          this.variants,
          selectedOptions,
          this.quantityOptionIndex,
          inputs.map((input) => input.value)
        );

        tiers.forEach((tier, index) => {
          const label = this.tierGroup.querySelector(`label[for="${CSS.escape(inputs[index].id)}"]`);
          const total = label.querySelector('[data-tier-total]');
          const unit = label.querySelector('[data-tier-unit]');
          const save = label.querySelector('[data-tier-save]');

          if (!tier.variant) {
            total.textContent = this.dataset.unavailableLabel;
            unit.textContent = '';
            save.hidden = true;
            return;
          }

          total.textContent = formatPrice(tier.variant.price, this.dataset.locale, this.dataset.currency);
          unit.textContent = this.strings.tier_unit.replace(
            '__PRICE__',
            formatPrice(tier.unit, this.dataset.locale, this.dataset.currency)
          );
          save.hidden = tier.saved < 1;
          save.textContent = this.strings.tier_save.replace('__PERCENT__', tier.saved);
        });
      }

      // Marks values that lead nowhere, without ever removing an input from the tab order.
      updateAvailability(selectedOptions) {
        this.optionGroups.forEach((group, position) => {
          group.querySelectorAll('input').forEach((input) => {
            const candidate = [...selectedOptions];
            candidate[position] = input.value;
            const variant = findVariant(this.variants, candidate);
            input.classList.toggle('disabled', !variant?.available);
          });
        });
      }

      // Dropping a file has to end up in the input itself: that is what carries it into the
      // multipart FormData Dawn sends to the cart.
      bindDropzone() {
        if (!this.dropzone) return;

        ['dragenter', 'dragover'].forEach((type) =>
          this.dropzone.addEventListener(type, (event) => {
            event.preventDefault();
            this.dropzone.classList.add('is-dragging');
          })
        );

        ['dragleave', 'dragend', 'drop'].forEach((type) =>
          this.dropzone.addEventListener(type, () => this.dropzone.classList.remove('is-dragging'))
        );

        this.dropzone.addEventListener('drop', (event) => {
          event.preventDefault();
          const [file] = event.dataTransfer?.files || [];
          if (!file) return;

          const transfer = new DataTransfer();
          transfer.items.add(file);
          this.fileInput.files = transfer.files;
          this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      requireArtwork(event) {
        if (this.fileInput.files.length > 0) return;

        event.preventDefault();
        event.stopPropagation();
        this.fileError.textContent = this.strings.missing;
        this.fileError.hidden = false;
        this.fileInput.focus();
      }

      updateOutline(selectedColor) {
        const enabled = hasOutline(selectedColor.dataset.outlineColor);
        this.outlineThickness.hidden = !enabled;
        // Disabled, not just hidden: that is what keeps the thickness out of the FormData when
        // the client wants no outline at all.
        this.thicknessInputs.forEach((input) => {
          input.disabled = !enabled;
        });
        this.schedulePreview();
      }

      releaseArtwork() {
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = '';
        this.artwork = null;
        this.artworkRatio = 1;
        this.fileNotices = [];
      }

      // Nothing leaves the browser: the file is only ever read locally and shown from a blob URL.
      async loadArtwork(file) {
        this.releaseArtwork();
        this.fileError.hidden = true;
        this.fileName.hidden = !file;
        this.fileName.textContent = file ? file.name : '';
        this.updateSubmitState();

        if (!file) {
          this.renderPreview();
          return;
        }

        const isSvg = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
        const isPng = file.type === 'image/png' || /\.png$/i.test(file.name);

        if (!isSvg && !isPng) {
          this.fileNotices = ['unsupported'];
          this.renderPreview();
          return;
        }

        const notices = [];
        let declaredRatio = 0;

        if (isSvg) {
          try {
            const geometry = readSvgGeometry(await file.text());
            declaredRatio = geometry.ratio;
            if (geometry.hasForeignObject) notices.push('foreign_object');
          } catch (error) {
            notices.push('unreadable');
          }
        }

        const url = URL.createObjectURL(file);
        const image = new Image();

        try {
          // An uploaded SVG is only ever rendered through <img src="blob:…">, where scripts and
          // external references are inert. It is never injected as markup.
          await new Promise((resolve, reject) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', reject, { once: true });
            image.src = url;
          });
        } catch (error) {
          URL.revokeObjectURL(url);
          this.fileNotices = ['unreadable'];
          this.renderPreview();
          return;
        }

        this.previewUrl = url;
        this.artwork = image;

        const intrinsicRatio =
          image.naturalWidth > 0 && image.naturalHeight > 0 ? image.naturalWidth / image.naturalHeight : 0;

        if (isSvg) {
          // A browser invents a default size for an SVG that declares none (Chromium answers
          // 300x150), so its intrinsic ratio cannot be trusted: only the file's own viewBox or
          // width/height can, and its absence is worth saying out loud.
          this.artworkRatio = declaredRatio || intrinsicRatio || 1;
          if (!declaredRatio) notices.push('no_dimensions');
        } else {
          this.artworkRatio = intrinsicRatio || 1;
          if (!intrinsicRatio) notices.push('no_dimensions');
        }

        if (this.detectFlattenedArtwork(image)) notices.push('opaque');

        this.fileNotices = notices;
        this.renderPreview();
      }

      // A flattened PNG is common and must be said out loud: the outline will trace the file's
      // rectangle instead of the design. Returns false when the probe cannot be read.
      detectFlattenedArtwork(image) {
        try {
          const probe = document.createElement('canvas');
          const ratio = this.artworkRatio;
          probe.width = Math.max(1, Math.round(ratio >= 1 ? PROBE_SIZE : PROBE_SIZE * ratio));
          probe.height = Math.max(1, Math.round(ratio >= 1 ? PROBE_SIZE / ratio : PROBE_SIZE));
          const context = probe.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0, probe.width, probe.height);
          const { data } = context.getImageData(0, 0, probe.width, probe.height);
          return transparencyRatio(data) < TRANSPARENCY_THRESHOLD;
        } catch (error) {
          return false;
        }
      }

      schedulePreview() {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.renderPreview());
      }

      renderPreview() {
        this.renderNotices();

        if (!this.artwork) {
          this.canvas.hidden = true;
          this.previewScale.hidden = true;
          this.stageMessage.hidden = false;
          this.stageMessage.textContent = this.strings.empty;
          return;
        }

        const stagePx = Math.max(Math.min(this.stage.clientWidth, this.stage.clientHeight) * 0.86, 1);
        const motifMm = parseSizeMm(this.selectedSizeValue);
        const geometry = previewGeometry({
          motifMm,
          thicknessMm: this.thicknessMm,
          ratio: this.artworkRatio,
          maxMotifMm: this.largestSizeMm,
          stagePx,
        });

        const pixelRatio = Math.min(globalThis.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);
        this.canvas.width = Math.max(1, Math.round(geometry.width * pixelRatio));
        this.canvas.height = Math.max(1, Math.round(geometry.height * pixelRatio));
        this.canvas.style.width = `${geometry.width}px`;
        this.canvas.style.height = `${geometry.height}px`;
        this.canvas.hidden = false;
        this.stageMessage.hidden = true;

        const context = this.canvas.getContext('2d');
        context.clearRect(0, 0, this.canvas.width, this.canvas.height);

        const ink = OUTLINE_INK[this.outlineColor];
        if (ink) drawOutline(context, this.artwork, geometry, ink, pixelRatio);

        context.drawImage(
          this.artwork,
          geometry.outline * pixelRatio,
          geometry.outline * pixelRatio,
          geometry.artWidth * pixelRatio,
          geometry.artHeight * pixelRatio
        );

        const sizeValue = this.selectedSizeValue;
        this.previewScale.hidden = !sizeValue;
        if (sizeValue) {
          this.previewScale.textContent = this.strings.scale
            .replace('__SIZE__', sizeValue)
            .replace('__OUTLINE__', formatMm(geometry.outlineMm, this.dataset.locale))
            .replace('__FINISHED__', formatCm(geometry.finishedMm, this.dataset.locale));
        }
      }

      renderNotices() {
        const keys = [...this.staticNotices, ...this.fileNotices].filter((key) => this.strings[key]);
        this.previewNotices.replaceChildren(
          ...keys.map((key) => {
            const item = document.createElement('li');
            item.textContent = this.strings[key];
            return item;
          })
        );
        this.previewNotices.hidden = keys.length === 0;
      }
    }
  );
})();
